import type { $Fetch } from "ofetch";

/**
 * Nome do Controller da API
 */
const controller = "/AgendamentoWeb";

/**
 * Classe de gerenciamento de chamadas da API do AgendamentoWeb
 *
 * @class
 */
export default class AgendamentoWebApi {
  /**
   * Instância da dependência da API (ofetch)
   */
  private api;
  private tokenAgendamento: string | null = null;

  /**
   * Cria uma nova instância da classe AgendamentoWebApi.
   * @constructor
   * @param api - Instância da dependência da API (ofetch).
   */
  constructor(api: $Fetch) {
    this.api = ((request: string, options: any = {}) => {
      return api(request, {
        ...options,
        headers: {
          ...this.authHeaders(),
          ...(options?.headers || {}),
        },
      });
    }) as $Fetch;
  }

  setTokenAgendamento(token: string | null | undefined) {
    const tokenValue = (token || "").trim();
    this.tokenAgendamento = tokenValue
      ? tokenValue.startsWith("Bearer ")
        ? tokenValue
        : `Bearer ${tokenValue}`
      : null;
  }

  private authHeaders() {
    return this.tokenAgendamento
      ? { Authorization: this.tokenAgendamento }
      : {};
  }

  acessoLoginAgendamentoWeb(tokenAgendamento?: string) {
    const token = (tokenAgendamento || "")
      .replace(/^Bearer\s+/i, "")
      .trim();

    return this.api<any>(`${controller}/Acesso/loginAgendamentoWeb`, {
      method: "POST",
      headers: token ? { tokenAgendamento: token } : {},
    });
  }
  agendamento(codAgendamento: number) {
    return this.api<ApplicationModule[]>(`${controller}/Agendamento`, {
      method: "GET",
      params: { codAgendamento },
    });
  }
  agendamentoBuscar(codAgendamento: number) {
    return this.api<ApplicationModule[]>(`${controller}/Agendamento/Buscar`, {
      method: "GET",
      params: { codAgendamento },
    });
  }
  procedimento(codProcedimento: number) {
    return this.api<ApplicationModule[]>(`${controller}/Procedimento`, {
      method: "GET",
      params: { codProcedimento },
    });
  }
  horarioWebListar(
    codProcedimento: number,
    dataInicio: string,
    dataFim: string,
    dataNasc: string,
    horaInicio: string,
    horaFim: string,
    codMedico: number,
    codUnidade: number,
    codPlano: number,
    codEspecialidade: number | string,
  ) {
    return this.api<ApplicationModule[]>(`${controller}/Horarios/Listar`, {
      method: "GET",
      params: {
        codProcedimento,
        dataInicio,
        dataFim,
        horaInicio,
        horaFim,
        dataNasc,
        codMedico,
        codUnidade,
        codPlano,
        codEspecialidade,
      },
    });
  }
  buscarCPF(cpf: string) {
    return this.api<ApplicationModule[]>(`${controller}/BuscarCPF`, {
      method: "GET",
      params: { cpf },
    });
  }

  BuscarCPF(CPF: string) {
    return this.buscarCPF(CPF);
  }
  gerarTokenPaciente(
    nome: string,
    codPaciente: number,
    dataNasc: string,
    email: string,
    telefone: string,
    cpf: string,
  ) {
    return this.api<ApplicationModule[]>(`${controller}/PacienteToken`, {
      method: "GET",
      params: { nome, codPaciente, dataNasc, email, telefone, cpf },
    });
  }
  async validarTokenPaciente(
    validarToken: TokenVerification,
  ): Promise<boolean> {
    try {
      const response = await this.api<boolean>(
        `${controller}/ValidarTokenPaciente`,
        {
          method: "POST",
          body: validarToken,
        },
      );
      return response;
    } catch (error) {
      console.error("Erro ao validar token:", error);
      return false;
    }
  }
  AgendamentoCancelar(codAgendamento: number) {
    return this.api<ApplicationModule[]>(
      `${controller}/Agendamento/CancelarAgendamento`,
      {
        method: "DELETE",
        params: { codAgendamento: codAgendamento },
      },
    );
  }
  ProcedimentoRecomendacoes(codProcedimento: number) {
    // O endpoint devolve diretamente o texto configurado para o procedimento.
    return this.api<string>(
      `${controller}/RecomendacaoProcedimento`,
      {
        method: "GET",
        params: { codProcedimento },
      },
    );
  }
  SalvarSiteConfig(site: any) {
    return this.api<ApplicationModule[]>(`${controller}/SiteConfig/Salvar`, {
      method: "POST",
      body: site,
    });
  }
  SalvarPesquisaConfig(pesquisa: any) {
    return this.api<ApplicationModule[]>(
      `${controller}/ConfigPesquisa/Salvar`,
      {
        method: "POST",
        body: pesquisa,
      },
    );
  }
  SalvarWeb(agendamento: any) {
    return this.api<ApplicationModule[]>(`${controller}/Agendamento/Salvar`, {
      method: "POST",
      body: { agendamento },
    });
  }
  ProcedimentoListar(
    codMedico: number,
    codOperadora: number,
    codPlano: number,
    codUnidade: number,
    codEspecialidade: number | string,
    tipoAtendimento: string,
  ) {
    return this.api<any>(`${controller}/ProcedPlanoOp/Listar`, {
      method: "GET",
      params: {
        codMedico,
        codOperadora,
        codPlano,
        codUnidade,
        codEspecialidade,
        tipoAtendimento,
      },
    });
  }
  EspecialidadeListar(
    codUnidade: number,
    codMedico: number,
    codProcedimento: number,
    codOperadora: number,
    codPlano: number,
    tipoAtendimento: string,
  ) {
    return this.api<any>(`${controller}/Especialidade/Listar`, {
      method: "GET",
      params: {
        codUnidade,
        codMedico,
        codProcedimento,
        codOperadora,
        codPlano,
        tipoAtendimento,
      },
    });
  }
  OperadoraListar(
    codMedico: number,
    codProcedimento: number,
    codUnidade: number,
    codEspecialidade: number,
    tipoAtendimento: string,
  ) {
    return this.api<any>(`${controller}/Operadora/Listar`, {
      method: "GET",
      params: {
        codMedico,
        codProcedimento,
        codUnidade,
        codEspecialidade,
        tipoAtendimento,
      },
    });
  }
  configEmailListar() {
    return this.api<any>(`${controller}/Email/Listar`, {
      method: "GET",
    });
  }
  planoListar(codOperadora: number) {
    return this.api<any>(`${controller}/Plano/Listar`, {
      method: "GET",
      params: { codOperadora },
    });
  }
  UnidadeListar(
    codUnidade: number,
    codMedico: number,
    codProcedimento: number,
    codPlano: number,
    codEspecialidade: number,
  ) {
    return this.api<any>(`${controller}/Unidade/Listar`, {
      method: "GET",
      params: {
        codUnidade,
        codMedico,
        codProcedimento,
        codPlano,
        codEspecialidade,
      },
    });
  }
  MedicoListar(
    codOperadora: number,
    codUnidade: number,
    codPlano: number,
    codProcedimento: number,
    codEspecialidade: number | string,
    tipoAtendimento: string,
  ) {
    console.log(
      codPlano,
      codProcedimento,
      codEspecialidade,
      codUnidade,
      tipoAtendimento,
    );
    return this.api<any>(`${controller}/Medico/Listar`, {
      method: "GET",
      params: {
        codOperadora,
        codUnidade,
        codPlano,
        codProcedimento,
        codEspecialidade,
        tipoAtendimento,
      },
    });
  }
  listarWebAgendamentoConfig() {
    return this.api<any>(`${controller}/ConfigWebAgendamento/Listar`, {
      method: "GET",
    });
  }
  listarConfigPesquisa() {
    return this.api<any>(`${controller}/ConfigPesquisa/Listar`, {
      method: "GET",
    });
  }
  pacienteSalvar(paciente: any) {
    return this.api<any>(`${controller}/Paciente/Salvar`, {
      method: "POST",
      body: paciente,
    });
  }

  docPedidoSalvar(file: File, codPaciente: number) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("codPaciente", codPaciente.toString());

    return this.api<any>(`${controller}/DocPedido/Salvar`, {
      method: "POST",
      body: formData,
    });
  }
  buscarAgendamentosPaciente(codPaciente: number) {
    return this.api<any>(`${controller}/Agendamento/Paciente`, {
      method: "GET",
      params: { codPaciente },
    });
  }
  webAgendamentoConfig(formData: FormData) {
    return this.api<any>(`${controller}/ConfigWebAgendamento/Salvar`, {
      method: "POST",
      body: formData,
    });
  }
  PesquisaConfig(formData: FormData) {
    return this.api<any>(`${controller}/ConfigPesquisa/Salvar`, {
      method: "POST",
      body: formData,
    });
  }
  enviarEmail(info: any) {
    return this.api<any>(`${controller}/Email/Enviar`, {
      method: "POST",
      body: info,
    });
  }
  enviarMensagemWhatsApp(request: { Numero: string; Mensagem: string }) {
    return this.api<any>(`${controller}/Whatsapp/EnviarMensagem`, {
      method: "POST",
      body: request,
    });
  }
  listarConfigSite() {
    return this.api<any>(`${controller}/ConfigSite/Listar`, {
      method: "GET",
    });
  }
  salvarConfigSite(formData: FormData) {
    return this.api<any>(`${controller}/SiteConfig/Salvar`, {
      method: "POST",
      body: formData,
    });
  }
}
